import {
  Widget,
  WidgetCloner,
  useTreeReferencesGlobalContext,
} from '@framework/engine';
import { message } from 'antd';
import { useCallback, useState } from 'react';
import { useDndEditorContext } from './useDndEditorContext';

export function useWidgetCloner() {
  const { removeWidget: editorRemoveWidget, insertWidget: editorInsertWidget } =
    useDndEditorContext();
  const treeGlobalContext = useTreeReferencesGlobalContext();

  const [copyLoading, setCopyLoading] = useState(false);
  const [cutLoading, setCutLoading] = useState(false);
  const [pasteLoading, setPasteLoading] = useState(false);

  const copyWidget = useCallback(async (widget: Widget) => {
    setCopyLoading(true);

    try {
      const cloned = WidgetCloner.copyWidget(widget);
      await navigator.clipboard.writeText(cloned);
      message.success('Widget copiato negli appunti');
    } catch (e) {
      console.error('[useWidgetCloner] Errore durante la copia del widget.', {e}); // prettier-ignore
      message.error('Errore durante la copia del widget. Riprovare.'); // prettier-ignore
    } finally {
      setCopyLoading(false);
    }
  }, []);

  const cutWidget = useCallback(
    async (widget: Widget) => {
      setCutLoading(true);

      try {
        const cutted = WidgetCloner.stringifyWidget(widget);
        await navigator.clipboard.writeText(cutted);
        editorRemoveWidget(widget);
        message.success('Widget copiato negli appunti');
      } catch (e) {
        console.error('[useWidgetCloner] Errore durante il taglio del widget.', {e}); // prettier-ignore
        message.error('Errore durante l\'operazione. Riprovare.'); // prettier-ignore
      } finally {
        setCutLoading(false);
      }
    },
    [editorRemoveWidget],
  );

  const pasteWidget = useCallback(
    async (parent: Widget) => {
      setPasteLoading(true);
      try {
        const stringWidget = await navigator.clipboard.readText();
        const result = WidgetCloner.getClonedWidget(stringWidget, treeGlobalContext?.allRoots); // prettier-ignore
        editorInsertWidget(result, parent);

        message.success('Widget incollato');
      } catch (e) {
        console.error('[useWidgetCloner] Errore durante l\'incolla del widget.', {e}); // prettier-ignore
        if (e instanceof SyntaxError) {
          message.error('Errore durante l\'operazione. Il widget copiato non è valido.'); // prettier-ignore
        } else {
          message.error('Errore durante l\'operazione. Riprovare.'); // prettier-ignore
        }
      } finally {
        setPasteLoading(false);
      }
    },
    [editorInsertWidget, treeGlobalContext?.allRoots],
  );

  return {
    copyWidget,
    cutWidget,
    pasteWidget,
    loadings: { copy: copyLoading, cut: cutLoading, paste: pasteLoading },
  };
}
